import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE_URL = '/api';

function SharedChatView() {
  const { shareToken } = useParams();
  const [chatData, setChatData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedChat();
  }, [shareToken]);

  const fetchSharedChat = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/chats/shared/${shareToken}`);
      const data = await response.json();

      if (data.success) {
        setChatData(data.chat);
        setMessages(data.messages);
      } else {
        setError('Chat not found or not shared');
      }
    } catch (err) {
      setError('Error loading shared chat');
      console.error('Error fetching shared chat:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatMessageText = (text) => {
    if (!text) return '';
    return text.replace(/\[\d+(?:,\s*\d+)*\]/g, '');
  };

  const isImageRelevant = (paragraph, image) => {
    if (!image.description) return false;

    const paraLower = paragraph.toLowerCase();
    const descLower = image.description.toLowerCase();

    const keywords = [
      'x-ray', 'radiograph', 'ct', 'scan', 'image', 'diagram', 'anatomy', 'structure',
      'tooth', 'teeth', 'root', 'canal', 'pulp', 'bone', 'tissue', 'lesion', 'infection',
      'swelling', 'abscess', 'periapical', 'endodontic', 'restoration', 'crown', 'bridge',
      'implant', 'extraction', 'surgery', 'treatment', 'diagnosis', 'condition', 'disease'
    ];

    const paraHasImageTerms = keywords.some(kw => paraLower.includes(kw));

    const descWords = descLower
      .split(/\s+/)
      .filter(w => w.length > 4 && !['this', 'that', 'with', 'from', 'show', 'shows', 'image'].includes(w));

    const hasMatch = descWords.some(word => paraLower.includes(word));

    return paraHasImageTerms && hasMatch;
  };

  const renderMessageWithInlineImages = (message) => {
    if (message.role === 'user') {
      return <p style={{ margin: 0, lineHeight: '1.6' }}>{message.content}</p>;
    }

    const cleanedText = formatMessageText(message.content);
    const paragraphs = cleanedText.split('\n').filter(p => p.trim());

    const images = message.images || [];
    const usedImageIndices = new Set();

    return (
      <div>
        {paragraphs.map((para, pIndex) => (
          <div key={pIndex} style={{ marginBottom: '16px' }}>
            <p style={{ margin: 0, lineHeight: '1.6', marginBottom: '12px' }}>{para}</p>

            {images.map((img, imgIndex) => {
              if (usedImageIndices.has(imgIndex)) return null;

              if (isImageRelevant(para, img)) {
                usedImageIndices.add(imgIndex);
                return (
                  <div key={imgIndex} style={{
                    marginTop: '12px',
                    marginBottom: '12px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <img
                      src={img.presigned_url || img.url}
                      alt={img.description || 'Medical reference image'}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '4px',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                    <div style={{
                      marginTop: '8px',
                      fontSize: '13px',
                      color: '#666',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontWeight: '500' }}>{img.book_name}</div>
                      <div style={{ fontStyle: 'italic' }}>{img.author}</div>
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#666' }}>Loading shared chat...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#e74c3c', marginBottom: '12px' }}>{error}</div>
          <div style={{ fontSize: '14px', color: '#666' }}>This chat may not be shared or the link is invalid.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      {/* Top Logo Bar */}
      <div style={{
        backgroundColor: 'white',
        padding: '16px 24px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <img src="/logo.svg" alt="S.C.A.L.P.E.L." style={{ height: '36px' }} />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px'
        }}>
          <p style={{
            margin: 0,
            fontSize: '12px',
            color: '#666',
            fontWeight: '400'
          }}>a product of</p>
          <a href="https://docplix.com" target="_blank" rel="noopener noreferrer">
            <img
              src={process.env.PUBLIC_URL + '/docplix.png'}
              alt="Docplix"
              style={{ height: '36px', display: 'block' }}
            />
          </a>
        </div>
      </div>

      <div style={{
        maxWidth: '900px',
        margin: '20px auto',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa'
        }}>
          <div style={{ fontSize: '20px', fontWeight: '600', color: '#333', marginBottom: '4px' }}>
            {chatData?.title || 'Shared Chat'}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Specialty: {chatData?.specialty || 'General'}
          </div>
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            backgroundColor: '#e3f2fd',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#1976d2'
          }}>
            This is a shared chat. You're viewing it in read-only mode.
          </div>
        </div>

        {/* Messages */}
        <div style={{ padding: '24px' }}>
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '40px 20px' }}>
              No messages in this chat yet.
            </div>
          ) : (
            messages.map((msg, index) => (
              <div key={index} style={{ marginBottom: '24px' }}>
                {/* User message */}
                {msg.role === 'user' && (
                  <div style={{
                    backgroundColor: '#e3f2fd',
                    padding: '14px 18px',
                    borderRadius: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#1976d2',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Question
                    </div>
                    {renderMessageWithInlineImages(msg)}
                  </div>
                )}

                {/* Assistant message */}
                {msg.role === 'assistant' && (
                  <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '14px 18px',
                    borderRadius: '12px'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#666',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Answer
                    </div>
                    {renderMessageWithInlineImages(msg)}

                    {/* References */}
                    {msg.references && msg.references.length > 0 && (
                      <div style={{
                        marginTop: '20px',
                        paddingTop: '16px',
                        borderTop: '1px solid #e0e0e0'
                      }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#333',
                          marginBottom: '12px'
                        }}>
                          References:
                        </div>
                        {msg.references.map((ref, refIndex) => (
                          <div key={refIndex} style={{
                            marginBottom: '12px',
                            padding: '10px',
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            border: '1px solid #e0e0e0'
                          }}>
                            {ref.source_text && (
                              <div style={{
                                fontSize: '13px',
                                color: '#444',
                                fontStyle: 'italic',
                                marginBottom: '6px',
                                lineHeight: '1.5'
                              }}>
                                "{ref.source_text}"
                              </div>
                            )}
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              <strong>{ref.book_name}</strong>
                              {ref.author && <span> - {ref.author}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Tables */}
                    {msg.tables && msg.tables.length > 0 && (
                      <div style={{ marginTop: '20px' }}>
                        {msg.tables.map((table, tableIndex) => (
                          <div key={tableIndex} style={{
                            marginBottom: '16px',
                            padding: '12px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0'
                          }}>
                            <img
                              src={table.presigned_url || table.url}
                              alt={table.description || 'Medical reference table'}
                              style={{
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: '4px',
                                display: 'block',
                                margin: '0 auto'
                              }}
                            />
                            <div style={{
                              marginTop: '8px',
                              fontSize: '13px',
                              color: '#666',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontWeight: '500' }}>{table.book_name}</div>
                              <div style={{ fontStyle: 'italic' }}>{table.author}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default SharedChatView;
